import adminroleService from '../../../services/adminroleService';
import filterService from '../../../services/filterService';

import {
    setAdminroleLoading,
    setAdminroleError,
    setAdminroleList,
    setAdminroleCurrent,
    setAdminrolePagination,
    setAdminroleSort,
    setAdminroleFilters,
    setAdminroleDefaultFilter,
    setAdminroleSettings,
    clearAdminroleCurrent,
    clearAdminroleError,
    setAdminroleFilterLoading,
    changeAdminroleFilterCondition,
    addAdminroleFilterCondition,
    updateAdminroleFilterConditionRule,
    updateAdminroleFilterConditionValue,
    removeAdminroleFilterCondition,
    setAdminroleSelected,
    setAdminroleDownloading,
    resetAdminroleFiltersAndPagination,

    setAdminroleClassicFilters
} from './adminroleSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().adminrole.isSettingsInitialized) {
            dispatch(setAdminroleLoading(true));
            const response = await adminroleService.getSettings();

            // Set the settings
            dispatch(setAdminroleSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setAdminroleClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setAdminroleDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        dispatch(setAdminroleError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setAdminroleLoading(false));
    }
};

export const fetchAdminroles = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().adminrole;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setAdminroleLoading(true));
        const response = await adminroleService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setAdminroleList(response));
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setAdminroleError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAdminroleLoading(false));
    }
};

export const fetchAdminrole = (id) => async (dispatch) => {
    try {
        dispatch(setAdminroleLoading(true));
        const response = await adminroleService.getOne(id);
        dispatch(setAdminroleCurrent(response.data));
    } catch (error) {
        dispatch(setAdminroleError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setAdminroleLoading(false));
    }
};

export const createAdminrole = (data) => async (dispatch) => {
    try {
        const { permissions, ...dataWithoutPermissions } = data;
        const response = await adminroleService.create(dataWithoutPermissions);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateAdminrole = (id, data) => async (dispatch) => {
    try {
        const { permissions, ...dataWithoutPermissions } = data;
        const response = await adminroleService.update(id, dataWithoutPermissions);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteAdminrole = (id) => async (dispatch) => {
    try {
        dispatch(setAdminroleLoading(true));
        const response = await adminroleService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setAdminroleError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAdminroleLoading(false));
    }
};

export const downloadAdminrole = (type) => async (dispatch, getState) => {
    try {
        const state = getState().adminrole;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setAdminroleDownloading(true));
        const response = await adminroleService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setAdminroleDownloading(false));

        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setAdminroleError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAdminroleDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setAdminrolePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setAdminroleSort(sort));
};

export const searchAdminroles = (query) => (dispatch, getState) => {
    const currentFilters = getState().adminrole.filters;
    dispatch(setAdminroleFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setAdminroleDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeAdminroleFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addAdminroleFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateAdminroleFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateAdminroleFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeAdminroleFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setAdminroleFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().adminrole.settings;
            dispatch(setAdminroleSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setAdminroleFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setAdminroleFilterLoading(false));
        dispatch(setAdminroleError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setAdminroleSelected(selectedRows));
    };
};

export const clearCurrentAdminrole = () => {
    return (dispatch) => {
        dispatch(clearAdminroleCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetAdminroleFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().adminrole.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setAdminroleClassicFilters(updatedClassicFilters));
};